import { useState } from "react";
import { MapPin, Crosshair, Search, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useLoc, geocode } from "@/lib/location";

export const RADII = [0.5, 1, 2, 5, 10, 25, 50];

export const RadiusChips = ({ value, onChange }) => (
  <div className="flex flex-wrap gap-2" data-testid="location-radius-select">
    {RADII.map((r) => (
      <button key={r} data-testid={`radius-${r}`} onClick={() => onChange(r)}
        className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${value === r ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}>
        {r < 1 ? `${r * 1000} m` : `${r} km`}
      </button>
    ))}
  </div>
);

export function LocationPicker({ open, onOpenChange }) {
  const { loc, setLoc, radius, setRadius, detect } = useLoc();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  const search = async (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setBusy(true);
    try {
      setResults(await geocode(q));
    } finally {
      setBusy(false);
    }
  };
  const pick = (r) => {
    setLoc(r);
    setResults([]);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="location-dialog">
        <DialogHeader><DialogTitle className="font-heading">Choose your location</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Currently: <b className="text-slate-800">{loc.label}</b></p>
        <button data-testid="location-use-gps" onClick={async () => (await detect()) && onOpenChange(false)}
          className="flex items-center gap-2 w-full rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 font-semibold hover:bg-red-100">
          <Crosshair className="w-4 h-4" /> Use my current location
        </button>
        <form onSubmit={search} className="flex gap-2">
          <Input data-testid="location-search-input" placeholder="Search area, e.g. Velachery Chennai" value={q} onChange={(e) => setQ(e.target.value)} />
          <button data-testid="location-search-button" className="px-4 rounded-md bg-slate-900 text-white">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}</button>
        </form>
        <div className="max-h-48 overflow-auto space-y-1">
          {results.map((r, i) => (
            <button key={i} data-testid={`location-result-${i}`} onClick={() => pick(r)} className="flex items-start gap-2 w-full text-left p-2 rounded-lg hover:bg-slate-50 text-sm">
              <MapPin className="w-4 h-4 mt-0.5 text-red-500 shrink-0" /> {r.label}
            </button>
          ))}
        </div>
        <div className="space-y-2">
          <p className="text-sm font-semibold text-slate-700">Show deals within</p>
          <RadiusChips value={radius} onChange={setRadius} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
